import Dexie from 'dexie';
import { Injectable } from '@angular/core';
import { PosItem } from '../models/pos-item.model';
import { PosCardWalletCache, PosOrder } from '../models/pos-order.model';

export class PosDB extends Dexie {
  items: Dexie.Table<PosItem, number>;
  orders: Dexie.Table<PosOrder, string>;
  cards: Dexie.Table<PosCardWalletCache, number>;
  constructor() {
    super('PosDatabase');
    this.version(1).stores({
      items: '++id, name, categoryId, active',
      orders: 'localId, orderDate, syncStatus'
    });
    this.version(2).stores({
      items: '++id, name, categoryId, active',
      orders: 'localId, orderDate, syncStatus, terminalId, paymentMode, cardId',
      cards: 'id, warehouseid, card_uid, card_serial_no, holder_code, status, current_balance'
    });
    this.version(3).stores({
      items: '++id, itemid, whmasterid, name, categoryId, active',
      orders: 'localId, orderDate, syncStatus, terminalId, paymentMode, cardId',
      cards: 'id, warehouseid, card_uid, card_serial_no, holder_code, status, current_balance'
    });
    this.items = this.table('items');
    this.orders = this.table('orders');
    this.cards = this.table('cards');
  }
}


@Injectable({ providedIn: 'root' })
export class PosOfflineService {
  private db = new PosDB();

  // Items
  async bulkUpsertItems(items: PosItem[]) {
    // replace local items with fresh master data (simple approach)
    await this.db.transaction('rw', this.db.items, async () => {
      await this.db.items.clear();
      await this.db.items.bulkAdd(items);
    });
  }
  getAllItems() {
    return this.db.items.toArray();
  }

  async adjustItemStocks(
    orderItems: Array<{ itemId?: number; qty?: number }> = [],
    terminalId?: number | null,
    direction: 'decrease' | 'increase' = 'decrease'
  ) {
    if (!orderItems.length) {
      return;
    }

    const groupedQty = orderItems.reduce((map, row) => {
      const itemId = Number(row?.itemId || 0);
      const qty = Number(row?.qty || 0);
      if (!itemId || !qty) {
        return map;
      }

      map.set(itemId, Number((map.get(itemId) || 0) + qty));
      return map;
    }, new Map<number, number>());

    if (!groupedQty.size) {
      return;
    }

    const multiplier = direction === 'increase' ? 1 : -1;

    await this.db.transaction('rw', this.db.items, async () => {
      for (const [itemId, qty] of groupedQty.entries()) {
        const item = await this.db.items
          .toCollection()
          .filter((row) =>
            Number((row as any)?.itemid || 0) === Number(itemId)
            && (!terminalId || Number((row as any)?.whmasterid || 0) === Number(terminalId))
          )
          .first();
        if (!item) {
          continue;
        }

        if (item.isnegativestockallow) {
          continue;
        }

        const currentBalance = Number(item.bqty || 0);
        const nextBalance = currentBalance + (qty * multiplier);
        item.bqty = Number(nextBalance.toFixed(2));
        await this.db.items.put(item);
      }
    });
  }

  async reapplyPendingOrderStock() {
    const pendingOrders = await this.getPendingOrders();
    for (const order of pendingOrders) {
      await this.adjustItemStocks(order?.items || [], Number(order?.terminalId || 0), 'decrease');
    }
  }

  // Cards
  async bulkUpsertCards(cards: PosCardWalletCache[], warehouseid: number) {
    const preparedCards = (cards || []).map((card) => ({
      ...card,
      warehouseid,
      current_balance: Number(card.current_balance || 0),
      cache_updated_at: new Date().toISOString()
    }));

    await this.db.transaction('rw', this.db.cards, async () => {
      await this.db.cards.where('warehouseid').equals(warehouseid).delete();
      if (preparedCards.length) {
        await this.db.cards.bulkPut(preparedCards);
      }
    });
  }

  async getCardsByWarehouse(warehouseid: number) {
    return this.db.cards.where('warehouseid').equals(warehouseid).toArray();
  }

  async findCardByScan(scanValue: string, warehouseid: number) {
    const normalizedScan = (scanValue || '').trim().toUpperCase();
    if (!normalizedScan) {
      return undefined;
    }

    return this.db.cards
      .where('warehouseid')
      .equals(warehouseid)
      .filter((card) =>
        [
          card.card_uid,
          card.card_serial_no,
          card.holder_code
        ]
          .filter(Boolean)
          .map((value) => String(value).trim().toUpperCase())
          .includes(normalizedScan)
      )
      .first();
  }

  async getCardById(cardId: number) {
    return this.db.cards.get(cardId);
  }

  async upsertCard(card: PosCardWalletCache) {
    const preparedCard = {
      ...card,
      current_balance: Number(card.current_balance || 0),
      cache_updated_at: new Date().toISOString()
    };

    await this.db.cards.put(preparedCard);
    return preparedCard;
  }

  async adjustCardBalance(cardId: number, amountDelta: number) {
    const card = await this.db.cards.get(cardId);
    if (!card) {
      return undefined;
    }

    const nextBalance = Math.max(0, Number(card.current_balance || 0) + Number(amountDelta || 0));
    card.current_balance = Number(nextBalance.toFixed(2));
    card.cache_updated_at = new Date().toISOString();
    await this.db.cards.put(card);
    return card;
  }

  // Orders
  async saveOrderOffline(order: PosOrder) {
    order.syncStatus = 'pending';
    await this.db.orders.put(order);
  }
  async getPendingOrders() {
    return this.db.orders.where('syncStatus').equals('pending').toArray();
  }
  async markOrderSynced(localId: string, serverId: number,code:string) {
    const order = await this.db.orders.get(localId);
    if (order) {
      order.syncStatus = 'synced';
      (order as any).id = serverId;
      (order as any).code = code;
      await this.db.orders.put(order);
    }
  }




  async getOrderByLocalId(localId: string): Promise<PosOrder | undefined> {
    return this.db.orders.get(localId);
  }
}
